import React, { Component } from 'react';
import { Box, Button, Paper, Typography, Snackbar, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

/**
 * ErrorBoundary component that catches React errors and provides recovery options.
 *
 * For DOM reconciliation errors (commonly caused by browser extensions modifying the DOM),
 * it shows a brief snackbar notification and auto-reloads the page.
 *
 * For other errors, it shows a full-page recovery UI with options to reload or go home.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isDOMError: false,
      showSnackbar: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a DOM reconciliation error
    const isDOMError =
      error?.message?.includes('removeChild') ||
      error?.message?.includes('insertBefore') ||
      error?.message?.includes('appendChild') ||
      error?.name === 'NotFoundError';

    return {
      hasError: true,
      error,
      isDOMError,
      showSnackbar: isDOMError,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // If it's a DOM error, schedule a reload after the snackbar displays
    if (this.state.isDOMError) {
      setTimeout(() => {
        window.location.reload();
      }, 3500);
    }
  }

  componentDidUpdate(prevProps) {
    // Optional `resetKeys`: when any of them changes (e.g. a widget is resized), retry rendering.
    const { resetKeys } = this.props;
    if (!this.state.hasError || !Array.isArray(resetKeys) || !Array.isArray(prevProps.resetKeys)) return;
    const changed = resetKeys.length !== prevProps.resetKeys.length || resetKeys.some((key, index) => key !== prevProps.resetKeys[index]);
    if (changed) this.handleReset();
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, isDOMError: false, showSnackbar: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleSnackbarClose = () => {
    this.setState({ showSnackbar: false });
    // Reload immediately when snackbar is dismissed
    window.location.reload();
  };

  render() {
    const { hasError, isDOMError, showSnackbar, error } = this.state;
    const { children, fallback } = this.props;

    // Optional scoped fallback (dashboard widgets): render it instead of the full-page recovery
    // UI so one failing widget cannot blank the whole page. DOM errors keep the reload path.
    if (hasError && !isDOMError && fallback !== undefined) {
      return typeof fallback === 'function' ? fallback({ error, reset: this.handleReset }) : fallback;
    }

    // For DOM errors, show snackbar and let it auto-reload
    if (hasError && isDOMError) {
      return (
        <>
          {children}
          <Snackbar
            open={showSnackbar}
            autoHideDuration={3000}
            onClose={this.handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity="info"
              variant="filled"
              onClose={this.handleSnackbarClose}
              sx={{ width: '100%' }}
            >
              Tapahtui odottamaton virhe. Sivu päivitetään.
            </Alert>
          </Snackbar>
        </>
      );
    }

    // For other errors, show full recovery UI
    if (hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 480,
              width: '100%',
              textAlign: 'center',
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Jokin meni pieleen
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Sovelluksessa tapahtui odottamaton virhe. Voit yrittää ladata sivun uudelleen tai palata etusivulle.
            </Typography>

            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {error.toString()}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
              >
                Lataa uudelleen
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
              >
                Etusivulle
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
